import { useMutation, useQuery } from "convex/react";
import { useCallback } from "react";
import { extractAlwaysAllowToolName } from "../../../../email-assistant/core/askQuestionPermissions";
import {
	answerValues,
	formatAnswerMessage,
	type InteractionAnswer,
	type InteractionAnswerMap,
	type ParsedInteraction,
	toStructuredAnswers,
} from "../../../../email-assistant/core/toolInteractions";
import { api } from "../../../convex/_generated/api";
import {
	type PagedInteractionAnswerMap,
	PagedInteractionDock,
} from "./PagedInteractionDock";

interface Props {
	interaction: ParsedInteraction;
	onSubmit: (
		answerMessage: string,
		response: { toolName: string; answers: InteractionAnswer[] },
	) => void;
	disabled?: boolean;
}

function toInteractionAnswers(
	answers: PagedInteractionAnswerMap,
): InteractionAnswerMap {
	const converted: InteractionAnswerMap = {};
	for (const [id, answer] of Object.entries(answers)) {
		if (!answer) continue;
		converted[id] = { value: answer.value, typed: answer.typed };
	}
	return converted;
}

export function ActiveInteractionDock({
	interaction,
	onSubmit,
	disabled,
}: Props) {
	const toolMetadata = useQuery(api.users.listAutoApprovableTools);
	const toggleAutoApprove = useMutation(api.users.toggleAutoApproveTool);

	const submit = useCallback(
		async (pagedAnswers: PagedInteractionAnswerMap) => {
			const answers = toInteractionAnswers(pagedAnswers);
			const known = new Set(toolMetadata?.map((tool) => tool.name) ?? []);
			const toEnable: string[] = [];
			for (const answer of Object.values(answers)) {
				if (!answer) continue;
				const options = Array.isArray(answer.value)
					? answer.value
					: [answer.value];
				for (const option of options) {
					const name = extractAlwaysAllowToolName(option);
					if (name && known.has(name)) toEnable.push(name);
				}
			}
			await Promise.all(
				toEnable.map((name) =>
					toggleAutoApprove({ toolName: name, enabled: true }).catch(() => {}),
				),
			);
			onSubmit(formatAnswerMessage(interaction, answerValues(answers)), {
				toolName: interaction.toolName,
				answers: toStructuredAnswers(interaction, answers),
			});
		},
		[interaction, onSubmit, toggleAutoApprove, toolMetadata],
	);

	return (
		<PagedInteractionDock
			header={interaction.header}
			questions={interaction.questions.map((question) => ({
				id: question.id,
				question: question.question,
				kind: question.kind,
				options: question.options?.map((option) => ({
					value: option,
					label: option,
				})),
				allowFreeText: question.kind === "choice",
				multiSelect: question.multiSelect,
				optional: question.optional,
				placeholder: question.placeholder,
			}))}
			onSubmit={submit}
			disabled={disabled}
		/>
	);
}
